import { Dispatch, DragEvent, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { UploadState, uploadFile, uploadMultiFiles, verifyType } from "./uploadUtils";

type FileDnDWrapperBaseProps = {
  forType: string;
  forId: string;
  onDragOver?: (e: DragEvent<HTMLDivElement>) => void;
  onDragLeave?: () => void;
  onError?: (message: string) => void;
  children: React.ReactNode;
  containerStyle?: React.CSSProperties;
  setUploadState: Dispatch<SetStateAction<UploadState>>;
  accept?: string;
  disabled?: boolean;
}

type FileDnDWrapperProps =
  | ({
    allowMultiple?: false;
    onComplete: (
      savedName: string,
      fileName: string,
      signedDownloadRequest: string
    ) => void;
    onCompleteMultiple?: never;
  } & FileDnDWrapperBaseProps)
  | ({
    allowMultiple: true;
    onCompleteMultiple: (
      files: {
        savedName: string;
        fileName: string;
        signedDownloadRequest: string;
      }[]
    ) => void;
    onComplete?: never;
  } & FileDnDWrapperBaseProps);

export const FileDnDWrapper = (props: FileDnDWrapperProps) => {
  const {
    forType,
    forId,
    onDragOver = () => {},
    onDragLeave = () => {},
    onError,
    children,
    containerStyle,
    setUploadState,
    accept,
    disabled,
    allowMultiple,
    onComplete,
    onCompleteMultiple,
  } = props;
  const [dragItem, setDragItem] = useState(false);
  const dropRef = useRef(null);
  const timeoutRef = useRef(null);

  const handleDragEvent = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const getFileFromEvent = useCallback((e) => {
    const files = [...e.dataTransfer.files];
    const filesWithPreview = [];

    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      file.preview = URL.createObjectURL(file);
      filesWithPreview.push(file);
    }

    if (allowMultiple) {
      return filesWithPreview;
    }

    return filesWithPreview[0];
  }, [allowMultiple]);

  const handleDragEnter = useCallback((e) => {
    if (disabled) return;
    handleDragEvent(e);
    setDragItem(null);
    onDragOver(e);
  }, [setDragItem, onDragOver, disabled]);

  const handleDragOver = useCallback((e) => {
    if (disabled) return;
    handleDragEvent(e);
    clearTimeout(timeoutRef.current);
    setDragItem(null);
    onDragOver(e);
  }, [timeoutRef, setDragItem, onDragOver, disabled]);

  const handleDragLeave = useCallback((e) => {
    if (disabled) return;
    handleDragEvent(e);
    timeoutRef.current = window.setTimeout(() => { setDragItem(null); onDragLeave(); }, 150);
  }, [timeoutRef, setDragItem, onDragLeave, disabled]);

  const handleDrop = useCallback((e) => {
    if (disabled) return;
    handleDragEvent(e);
    const uploadItem = getFileFromEvent(e);
    if (uploadItem) {
      setDragItem(uploadItem);
      if (accept) {
        const { type } = uploadItem;
        const isAllowed = verifyType(accept, type);
        if (!isAllowed) {
          return;
        }
      }
      const file = getFileFromEvent(e);
      if (allowMultiple) {
        uploadMultiFiles(file, setUploadState, forType, forId, onCompleteMultiple, onError);
      } else {
        uploadFile(getFileFromEvent(e), setUploadState, forType, forId, onComplete, onError);
      }
      onDragLeave();
    } else onDragLeave();
  }, [disabled, getFileFromEvent, onDragLeave, accept, allowMultiple, setUploadState, forType, forId, onCompleteMultiple, onError, onComplete]);

  useEffect(() => {
    const dropContainer = dropRef.current;
    dropContainer.addEventListener('dragenter', handleDragEnter);
    dropContainer.addEventListener('dragleave', handleDragLeave);
    dropContainer.addEventListener('dragover', handleDragOver);
    dropContainer.addEventListener('drop', handleDrop);
    return () => {
      dropContainer.removeEventListener('dragenter', handleDragEnter);
      dropContainer.removeEventListener('dragleave', handleDragLeave);
      dropContainer.removeEventListener('dragover', handleDragOver);
      dropContainer.removeEventListener('drop', handleDrop);
    };
  }, [handleDragEnter, handleDragLeave, handleDragOver, handleDrop]);

  useEffect(() => {
    if (dragItem) {
      onDragLeave();
    }
  }, [dragItem, setUploadState, forType, forId, onComplete, onError, onDragLeave]);

  return (
    <div ref={dropRef} style={containerStyle}>
      {children}
    </div>
  );
};

export default FileDnDWrapper;
